import { useCallback, useEffect, useMemo, useState } from 'react';

import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { array, object, string, mixed } from 'yup';

import map from 'lodash/map';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';

import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

import { ReactComponent as CloseIcon } from 'src/images/Close_gray.svg';

import AutoCompleteField from 'src/components/AutoCompleteField';
import TextField from 'src/pages/ProjectDetailsPage/components/JobForm/components/TextField';
import DateField from 'src/pages/ProjectDetailsPage/components/JobForm/components/DateField';
import NumberField from 'src/pages/ProjectDetailsPage/components/JobForm/components/NumberField';
import BooleanField from 'src/pages/ProjectDetailsPage/components/JobForm/components/BooleanField';
import MultiSelectField from 'src/pages/ProjectDetailsPage/components/JobForm/components/MultiSelectField';

import messages from './messages';

type FieldOption =
  | string
  | { value: string }
  | { label?: string; value: string }
  | { label?: string; value: string }[]
  | { options?: string[] }
  | null;

export interface AdvancedFiltersState {
  name?: string;
  operator?: string;
  value?: string | Array<string>;
}

export interface AdvancedFiltersProps {
  open: boolean;
  filters: AdvancedFiltersState[];
  handleSearch: (filters: AdvancedFiltersState[]) => void;
}

interface AdvancedFiltersData {
  fields: {
    label: string;
    value: string;
  }[];
  types: {
    [key: string]: string;
  };
  operators: {
    [key: string]: { label: string; value: string }[];
  };
  options: {
    [key: string]: { version: string; uri: string };
  };
}

interface Props {
  open: boolean;
  data?: AdvancedFiltersData;
  filters: AdvancedFiltersState[];
  handleSearch: (filters: AdvancedFiltersState[]) => void;
  setFilters: (filters: AdvancedFiltersState[]) => void;
  dynamicOptions?: (options: { version: string; uri: string }) => void;
}

const paperStylesSx = { width: '100%', borderRadius: 2 };

export default function AdvancedFilters({
  open,
  data,
  filters,
  handleSearch,
  setFilters,
  dynamicOptions,
}: Props) {
  const { formatMessage } = useIntl();

  const [validateOnChange, setValidateOnChange] = useState(false);

  const initialValues = useMemo(() => {
    return filters.map(({ name, value, operator }) => ({
      name: name || '',
      value: value || '',
      operator: operator || '',
    }));
  }, [filters]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validateOnChange: validateOnChange,
    validationSchema: array().of(
      object().shape({
        name: string().required(),
        operator: string().required(),
        value: mixed()
          .required()
          .test('is-valid-value', 'Value is required', function (value) {
            if (typeof value === 'string') return !isEmpty(value);
            if (Array.isArray(value)) return value.length > 0;
            return false;
          }),
      }),
    ),
    onSubmit: () => undefined,
  });

  useEffect(() => {
    if (!open) {
      setValidateOnChange(false);
    }
  }, [open]);

  const extractValue = useCallback((option: FieldOption): string | string[] => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    if (typeof option === 'object' && 'value' in option) return option.value;
    if ('options' in option && isArray(option.options)) return option.options;
    if (Array.isArray(option)) return option.map((item) => item.value);
    return '';
  }, []);

  const updateFilter = useCallback(
    (index: number, updates: Partial<AdvancedFiltersState>) => {
      filters[index] = {
        ...filters[index],
        ...updates,
      };
      setFilters(filters);
      formik.setFieldValue(index.toString(), { ...filters[index] });
    },
    [filters],
  );

  const handleNameChange = useCallback(
    (index: number, option: FieldOption) => {
      const value = extractValue(option) as string;
      updateFilter(index, { name: value, operator: '', value: '' });
    },
    [updateFilter],
  );

  const handleOperatorChange = useCallback(
    (index: number, option: FieldOption) => {
      const value = extractValue(option) as string;
      updateFilter(index, { operator: value });
    },
    [updateFilter],
  );

  const handleValueChange = useCallback(
    (index: number, option: FieldOption) => {
      const value = extractValue(option);
      updateFilter(index, { value });
    },
    [updateFilter],
  );

  const handlePressSearch = useCallback(() => {
    setValidateOnChange(true);
    formik.validateForm().then((errors) => {
      const isValid = isEmpty(errors);

      if (isValid) {
        handleSearch(filters);
      }
    });
  }, [handleSearch, filters]);

  const handleAddFilter = useCallback(() => {
    const newFilters = [...filters, {}];
    setFilters(newFilters);
    formik.setValues(
      newFilters.map(({ name, value, operator }) => ({
        name: name || '',
        value: value || '',
        operator: operator || '',
      })),
    );
  }, [filters]);

  const handleClearAllFilters = useCallback(() => {
    formik.resetForm();
    handleSearch([]);
  }, [handleSearch]);

  const handleClearFilter = useCallback(
    (index: number) => {
      const updatedFilters = [...filters];
      updatedFilters.splice(index, 1);
      if (updatedFilters.length === 0) {
        updatedFilters.push({});
      }

      setFilters(updatedFilters);
      formik.setValues(
        updatedFilters.map(({ name, value, operator }) => ({
          name: name || '',
          value: value || '',
          operator: operator || '',
        })),
      );
    },
    [filters],
  );

  const isSelectField = useCallback((type?: string) => {
    return type === 'single-selectable' || type === 'multi-selectable';
  }, []);

  const buildOptionsProp = useCallback(
    (filterName?: string | null) => {
      if (!filterName) return null;
      if (!isSelectField(data?.types[filterName])) return null;
      const fieldOptions = data?.options[filterName];
      if (isArray(fieldOptions)) return { options: fieldOptions };
      if (fieldOptions?.uri && dynamicOptions)
        return { options: dynamicOptions(fieldOptions) };
      return null;
    },
    [dynamicOptions, data?.types, data?.options],
  );

  if (!open) return null;

  return (
    <Paper sx={paperStylesSx}>
      <Grid size={12} container p={2} spacing={2}>
        <Grid container size={12}>
          {data &&
            map(filters, (filterOption, index) => (
              <Grid container key={index} size={12}>
                <Grid container size={filters.length > 1 ? 11 : 12}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <AutoCompleteField
                      size="medium"
                      label={formatMessage(messages.filterNameLabel)}
                      options={data?.fields}
                      onChange={(value) => handleNameChange(index, value)}
                      value={filterOption.name || ''}
                      variant="outlined"
                      error={!!formik.errors[index]?.name}
                      helperText={formik.errors[index]?.name}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <AutoCompleteField
                      size="medium"
                      label={formatMessage(messages.filterOperatorLabel)}
                      options={
                        filterOption.name
                          ? data?.operators[filterOption.name]
                          : []
                      }
                      onChange={(value) => handleOperatorChange(index, value)}
                      value={filterOption.operator || ''}
                      variant="outlined"
                      error={!!formik.errors[index]?.operator}
                      helperText={formik.errors[index]?.operator}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FilterValueField
                      type={data?.types[filterOption.name || ''] || 'text'}
                      label={formatMessage(messages.filterValueLabel)}
                      onChange={(_: unknown, value: FieldOption) =>
                        handleValueChange(index, value)
                      }
                      value={filterOption.value}
                      variant="outlined"
                      error={!!formik.errors[index]?.value}
                      helperText={formik.errors[index]?.value}
                      {...buildOptionsProp(filterOption.name)}
                    />
                  </Grid>
                </Grid>
                {filters.length > 1 && (
                  <Grid
                    size={1}
                    container
                    alignContent="center"
                    justifyItems="center"
                  >
                    <IconButton onClick={() => handleClearFilter(index)}>
                      <CloseIcon />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            ))}
        </Grid>
        <Grid size={12} container justifyContent="space-between">
          <Grid size={5}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddOutlinedIcon />}
              onClick={handleAddFilter}
            >
              {formatMessage(messages.addFilterButton)}
            </Button>
          </Grid>
          <Grid size={7} container spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClearAllFilters}
            >
              {formatMessage(messages.clearFiltersButton)}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePressSearch}
            >
              {formatMessage(messages.searchButton)}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}

// eslint-disable-next-line
const FilterValueField = (props: any) => {
  const { type, ...otherProps } = props;

  const resolveFieldComponent = useCallback(() => {
    switch (type) {
      case 'boolean':
        return BooleanField;
      case 'multi-selectable':
      case 'single-selectable':
        return MultiSelectField;
      case 'date':
        return DateField;
      case 'number':
      case 'numeric':
        return NumberField;
      default:
        return TextField;
    }
  }, [type]);

  const FieldComponent = resolveFieldComponent();

  // NumberField needs special prop structure
  if (type === 'number' || type === 'numeric') {
    const { label, value, onChange, ...others } = otherProps;

    return (
      <FieldComponent
        field={{ label: label || '', description: '' }}
        value={{ value: typeof value === 'string' ? value : '' }}
        onChange={(_, newValue: FieldOption) => onChange(null, newValue)}
        {...others}
        size="medium"
      />
    );
  }

  return <FieldComponent {...otherProps} field={otherProps} size="medium" />;
};
